import React, { useState, useEffect } from 'react';
import { Button, Card, Col, Form, FormFeedback, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { CardBody } from "reactstrap";
import { Formik } from 'formik';
import * as Yup from "yup";
import { toast } from 'react-toastify';
import { ReactComponent as PenIcon } from "../../../assets/utils/images/svgs/editPen.svg";
import GroupStatus from "Components/IconDropdown/Cross_Tick/GroupStatus";
import { ReactComponent as DeleteIcon } from "../../../assets/utils/images/svgs/Delete.svg";
import { useGeneralSettings, useTestEmailOrSms, useUpdateGeneralSettings } from 'Api/Admin/GeneralSetting';
import ScheduledSMS from './ScheduledSMS';
import ScheduledEmails from './ScheduledEmails';

const GeneralSettingsForm = () => {
    const [emailModal, setEmailModal] = useState(false);
    const [showPowerBiModal, setShowPowerBiModal] = useState(false);
    const [generalSettingPost, setGeneralSettingPost] = useState({});
    const [showRedirectEmailModal, setShowRedirectEmailModal] = useState(false);
    const [showRedirectSmsModal, setShowRedirectSmsModal] = useState(false);
    const [redirectSms, setRedirectSms] = useState("");
    const [redirectEmails, setRedirectEmails] = useState([]);
    const [newRedirectEmail, setNewRedirectEmail] = useState("");
    const [redirectEmailError, setRedirectEmailError] = useState("");
    const [smsModal, setSmsModal] = useState(false);
    const [emailData, setEmailData] = useState({
        email: "",
        emailError: ""
    })
    const [smsData, setSmsData] = useState({
        phoneNo: "",
        phoneError: ""
    })
    const [powerBiLink, setPowerBiLink] = useState({
        link: "",
        linkError: ""
    })

    const {
        data: settingsResponse,
        error,
        mutate: mutateGeneralSettings,
    } = useGeneralSettings();

    const {
        trigger: updateGeneralSettings,
        isMutating,
    } = useUpdateGeneralSettings();

    const generalSettings = settingsResponse?.data;


    const {
        trigger: testEmailOrSms,
        isMutating: testLoading,
    } = useTestEmailOrSms();

    useEffect(() => {
        if (generalSettings) {
            setPowerBiLink({
                link: generalSettings?.vPowerBiLink || "",
                linkError: ""
            });
        }
    }, [generalSettings?.vPowerBiLink]);

    useEffect(() => {
        if (error) {
            toast.error(error.message || "Something went wrong");
        }
    }, [error]);

    const handleSettingIconChange = async (field, data) => {
        const newValue = Number(data) === 1;

        const updatedSettings = {
            ...generalSettingPost,
            [field]: newValue,
        };

        setGeneralSettingPost(updatedSettings);

        try {
            const response = await updateGeneralSettings({
                data: {
                    iSettingId: generalSettingPost?.iSettingId,
                    [field]: newValue,
                },
            });

            if (response?.status) {
                toast.success(
                    response.message || "General settings updated successfully."
                );

                await mutateGeneralSettings(
                    current => {
                        if (!current?.data) {
                            return current;
                        }

                        return {
                            ...current,
                            data: {
                                ...current.data,
                                [field]: newValue,
                            },
                        };
                    },
                    false
                );
            } else {
                // Revert
                setGeneralSettingPost(generalSettingPost);
                toast.error("Something went wrong.");
            }
        } catch (error) {
            console.error("POST ERROR:", error);

            // Revert
            setGeneralSettingPost(generalSettingPost);
            toast.error("Something went wrong.");
        }
    };

    const handlePowerBiToggle = (data) => {
        const newValue = data === 1;

        setGeneralSettingPost(prev => ({
            ...prev,
            bPowerBiEnabled: newValue,
        }));
    };

    useEffect(() => {
        if (settingsResponse?.data) {
            setGeneralSettingPost(settingsResponse.data);
        }
    }, [settingsResponse]);

    useEffect(() => {
        if (generalSettings?.vEmailRedirect) {
            const emails = generalSettings.vEmailRedirect
                .split(",")
                .map(email => email.trim())
                .filter(Boolean);

            setRedirectEmails(emails);
        } else {
            setRedirectEmails([]);
        }
    }, [generalSettings?.vEmailRedirect]);

    const handleAddRedirectEmail = () => {
        const email = newRedirectEmail.trim();

        if (!email) {
            setRedirectEmailError("E-mailadres is vereist");
            return;
        }

        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailPattern.test(email)) {
            setRedirectEmailError("Ongeldig e-mailadres");
            return;
        }

        if (redirectEmails.includes(email)) {
            setRedirectEmailError("E-mailadres bestaat al");
            return;
        }

        setRedirectEmails(prev => [...prev, email]);
        setNewRedirectEmail("");
        setRedirectEmailError("");
    };

    const handleApplyRedirectEmails = async () => {
        try {
            const response = await updateGeneralSettings({
                data: {
                    iSettingId: generalSettingPost?.iSettingId,
                    vEmailRedirect: redirectEmails.join(","),
                },
            });

            if (response?.status) {
                toast.success("Redirect e-mailadressen succesvol bijgewerkt.");
                setShowRedirectEmailModal(false);

                await mutateGeneralSettings();
            } else {
                toast.error("Kan redirect e-mailadressen niet bijwerken.");
            }
        } catch (error) {
            console.error("Redirect email update error:", error);
            toast.error("Kan redirect e-mailadressen niet bijwerken.");
        }
    };

    const handleDeleteRedirectEmail = (index) => {
        setRedirectEmails(prev =>
            prev.filter((_, emailIndex) => emailIndex !== index)
        );
    };

    const handleApplyRedirectSms = async () => {
        try {
            const response = await updateGeneralSettings({
                data: {
                    iSettingId: generalSettingPost?.iSettingId,
                    vSmsRedirect: redirectSms,
                },
            });

            if (response?.status) {
                toast.success("Redirect SMS-nummer succesvol bijgewerkt.");

                setShowRedirectSmsModal(false);

                await mutateGeneralSettings();
            } else {
                toast.error("Kan redirect SMS-nummer niet bijwerken.");
            }
        } catch (error) {
            console.error("Redirect SMS update error:", error);
            toast.error("Kan redirect SMS-nummer niet bijwerken.");
        }
    };
    useEffect(() => {
        if (generalSettings) {
            setRedirectSms(generalSettings?.vSmsRedirect || "");
        }
    }, [generalSettings]);


    const emailDataValidationSchema = Yup.object().shape({
        subject: Yup.string().required("veld is verplicht"),
        content: Yup.string().required("veld is verplicht")
    });

    const smsDataValidationSchema = Yup.object().shape({
        smsContent: Yup.string().required("veld is verplicht")
    });

    const textChangeHandler = (e, type) => {
        const value = e.target.value;
        if (type === "email") {
            setEmailData(prev => ({ ...prev, email: value, emailError: "" }));
        }
        if (type === "sms") {
            setSmsData(prev => ({ ...prev, phoneNo: value, phoneError: "" }));
        }
        if (type === "link") {
            setPowerBiLink(prev => ({ ...prev, link: value, linkError: "" }));
        }
    };

    const validateEmailSmsLink = (type) => {
        if (type === "email") {
            const emails = emailData.email.split(",").map((email) => email.trim());
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const invalidEmails = emails.filter((email) => !emailPattern.test(email));
            if (!emailData.email) {
                setEmailData((prev) => ({ ...prev, emailError: "E-mailadres is vereist" }));
            }
            else if (invalidEmails.length > 0) {
                setEmailData((prev) => ({ ...prev, emailError: "Ongeldig e-mailadres" }));
            }
            else {
                setEmailData((prev) => ({ ...prev, emailError: "" }));
                setEmailModal(true)
                setSmsModal(false)
            }
        }
        if (type === "sms") {
            const phonePattern = /^[0-9\-\(\)\s]+$/;
            const phoneNumber = smsData.phoneNo.replace(/\D/g, "");
            if (!smsData.phoneNo) {
                setSmsData((prev) => ({ ...prev, phoneError: "Telefoonnummer is vereist" }));
            }
            else if (phoneNumber.length < 10 || phoneNumber.length > 16 || !phonePattern.test(smsData.phoneNo)) {
                setSmsData(prev => ({ ...prev, phoneError: "Het telefoonnummer moet uit 10 tot 16 cijfers bestaan." }))
            }
            else {
                setEmailData((prev) => ({ ...prev, phoneError: "" }));
                setSmsModal(true)
                setEmailModal(false)
            }
        }
        if (type === "link") {
            const linkPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/;
            if (!powerBiLink.link) {
                setPowerBiLink((prev) => ({ ...prev, linkError: "Link is vereist" }));
            }
            else if (!linkPattern.test(powerBiLink.link)) {
                setPowerBiLink((prev) => ({ ...prev, linkError: "Ongeldige link." }));
            }
            else {
                setPowerBiLink((prev) => ({ ...prev, linkError: "" }));
                saveLinkHandler();
            }
        }
    }
    const saveLinkHandler = async () => {
        try {
            const response = await updateGeneralSettings({
                data: {
                    iSettingId: generalSettingPost?.iSettingId,
                    vPowerBiLink: powerBiLink.link,
                },
            });

            if (response?.status) {
                toast.success("Link succesvol bijgewerkt.");


                setGeneralSettingPost(prev => ({
                    ...prev,
                    vPowerBiLink: powerBiLink.link,
                }));


                await mutateGeneralSettings(
                    current => {
                        if (!current?.data) {
                            return current;
                        }

                        return {
                            ...current,
                            data: {
                                ...current.data,
                                vPowerBiLink: powerBiLink.link,
                            },
                        };
                    },
                    false
                );

                setShowPowerBiModal(false);
            } else {
                toast.error("Kan de link niet opslaan.");
            }
        } catch (error) {
            console.error("Power BI link update error:", error);
            toast.error("Kan de link niet opslaan.");
        }
    };

    const handleSubmit = async (type, values, setErrors) => {
        let data = {}
        const errors = {};
        if (type === "email") {
            if (!values.subject) errors.subject = 'veld is verplicht';
            if (!values.content) errors.content = 'veld is verplicht';
            if (Object.keys(errors).length > 0) {
                setErrors(errors);
                return;
            }
            data = {
                type,
                sendTo: emailData.email,
                subject: values.subject,
                content: values.content
            }
        }
        if (type === "sms") {
            if (!values.smsContent) {
                setErrors({ smsContent: 'veld is verplicht' });
                return;
            }
            data = {
                type,
                sendTo: Number(smsData.phoneNo),
                subject: "",
                content: values.smsContent
            }
        }
        try {
            const response = await testEmailOrSms({
                data,
            });

            if (response?.status || response?.data?.status) {
                toast.success(
                    type === "email"
                        ? "E-mail succesvol verzonden"
                        : "SMS succesvol verzonden"
                );

                if (type === "email") {
                    setEmailModal(false);
                    setEmailData({
                        email: "",
                        emailError: "",
                    });
                } else {
                    setSmsModal(false);
                    setSmsData({
                        phoneNo: "",
                        phoneError: "",
                    });
                }
            } else {
                toast.error(
                    type === "email"
                        ? "Kan geen e-mail verzenden"
                        : "Kan geen SMS versturen"
                );
            }
        } catch (error) {
            toast.error(
                type === "email"
                    ? "Kan geen e-mail verzenden"
                    : "Kan geen SMS versturen"
            );
        }
    }

    return (
        <>


            <Row className="mb-4 general-setting">
                {/* Left Card */}
                <Col lg={6}>
                    <Card className="main-card " style={{ height: '100%' }}>
                        <CardBody>
                            <p className="py-2 mb-3" >E-mail/SMS - settings</p>

                            <Row>
                                <Col sm="6">
                                    <FormGroup>
                                        <Label>
                                            E-mail ingeschakeld :
                                        </Label>
                                    </FormGroup>
                                </Col>

                                <Col sm="6">
                                    <FormGroup>
                                        <div className="cursor-pointer ms-2">
                                            <GroupStatus
                                                key={generalSettingPost?.bEmailEnabled}
                                                data={generalSettingPost?.bEmailEnabled == true ? 1 : 0}
                                                iconChangeHandler={(field, data) =>
                                                    handleSettingIconChange("bEmailEnabled", data)
                                                }
                                            />
                                        </div>
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col sm="6">
                                    <FormGroup>
                                        <Label>
                                            SMS ingeschakeld :
                                        </Label>
                                    </FormGroup>
                                </Col>

                                <Col sm="6">
                                    <FormGroup>
                                        <div className="cursor-pointer ms-2">
                                            <GroupStatus
                                                key={generalSettingPost?.bSmsEnabled}
                                                data={generalSettingPost?.bSmsEnabled == true ? 1 : 0}
                                                iconChangeHandler={(field, data) =>
                                                    handleSettingIconChange("bSmsEnabled", data)
                                                }
                                            />
                                        </div>
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm="6">
                                    <FormGroup>
                                        <Label for="password_current">
                                            Redirect e-mail :
                                        </Label>
                                    </FormGroup>
                                </Col>

                                <Col sm="6">
                                    <FormGroup className="d-flex align-items-start">
                                        <Label
                                            for="password_current"
                                            className="redirect-email-text"
                                        >
                                            {generalSettings?.environment === "production"
                                                ? "No Redirection"
                                                : generalSettings?.vEmailRedirect !== ''
                                                    ? generalSettings?.vEmailRedirect
                                                    : '--'}
                                        </Label>

                                        <span
                                            role="button"
                                            onClick={() => setShowRedirectEmailModal(true)}
                                            className="redirect-email-edit"
                                        >
                                            <PenIcon size={16} />
                                        </span>
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm="6">
                                    <FormGroup>
                                        <Label for="password_current">
                                            Redirect SMS :
                                        </Label>
                                    </FormGroup>
                                </Col>
                                <Col sm="6">
                                    <FormGroup className='d-flex justify-content-between align-items-center'>
                                        <Label for="password_current">
                                            {generalSettings?.environment === "production" ? "No Redirection" : generalSettings?.vSmsRedirect
                                                !== '' ? generalSettings?.vSmsRedirect : '--'}
                                        </Label>
                                        <span
                                            role="button"
                                            onClick={() => setShowRedirectSmsModal(true)}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <PenIcon size={16} />
                                        </span>

                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm="6">
                                    <FormGroup>
                                        <Label for="password_current">
                                            Push-messages ingeschakeld :
                                        </Label>
                                    </FormGroup>
                                </Col>
                                <Col sm="6">
                                    <FormGroup>
                                        <div className="cursor-pointer ms-2">
                                            <GroupStatus
                                                data={generalSettings?.bPushMessagesEnabled == true ? 1 : 0}
                                                iconChangeHandler={(field, data) =>
                                                    handleSettingIconChange("bPushMessagesEnabled", data)
                                                }
                                            />
                                        </div>
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm="6">
                                    <FormGroup>
                                        <Label for="password_current" >
                                            Test e-mail :
                                        </Label>
                                    </FormGroup>
                                </Col>
                                <Col sm="6" className='d-flex gap-2 mb-3'>
                                    <div>
                                        <input name="sms" type="text" className="form-control py-2" placeholder="Enter email address" value={emailData.email} onChange={(e) => textChangeHandler(e, "email")} />
                                        {emailData?.emailError && <FormFeedback className='d-block'>{emailData?.emailError}</FormFeedback>}
                                    </div>
                                    <div>
                                        <Button className="btn-color-style opslaan-btn py-2" size="sm" outline color="primary" type="button" onClick={() => validateEmailSmsLink("email")}>
                                            Verzenden
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm="6">
                                    <FormGroup>
                                        <Label for="password_current">
                                            Test SMS :
                                        </Label>
                                    </FormGroup>
                                </Col>
                                <Col sm="6" className='d-flex gap-2 mb-3'>
                                    <div>
                                        <input name="sms" type="number" className="form-control py-2 input-num" placeholder="Enter phone number" value={smsData.phoneNo} onChange={(e) => textChangeHandler(e, "sms")} />
                                        {smsData?.phoneError && <FormFeedback className='d-block'>{smsData.phoneError}</FormFeedback>}
                                    </div>
                                    <div>
                                        <Button className="btn-color-style opslaan-btn py-2" size="sm" outline color="primary" type="button" onClick={() => validateEmailSmsLink("sms")}>
                                            Verzenden
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>

                {/* Right Side */}
                <Col lg={6} style={{ height: '100%' }}>
                    <div className="d-flex flex-column general-setting">

                        <Card className="main-card mb-3 ">
                            <CardBody>
                                <p className="py-2 mb-3">Overig</p>
                                <Row>
                                    <Col sm="6">
                                        <FormGroup>
                                            <Label for="password_current">
                                                Link Power Bi: :
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                    <Col sm="6">
                                        <FormGroup className="d-flex gap-3 align-items-center">
                                            <div
                                                className={`cursor-pointer ${generalSettingPost?.bPowerBiEnabled !== true ? "disabled" : ""
                                                    }`}
                                                style={{
                                                    pointerEvents:
                                                        generalSettingPost?.bPowerBiEnabled !== true ? "none" : "auto",
                                                }}
                                            >
                                                <GroupStatus
                                                    data={generalSettingPost?.bPowerBiEnabled === true ? 1 : 0}
                                                    iconChangeHandler={(field, data) => handlePowerBiToggle(data)}
                                                />

                                            </div>
                                            {generalSettingPost?.bPowerBiEnabled === true && (
                                                <span
                                                    role="button"
                                                    onClick={() => setShowPowerBiModal(true)}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <PenIcon size={16} />
                                                </span>
                                            )}

                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col sm="6">
                                        <FormGroup>
                                            <Label for="password_current">
                                                Google Maps afstandberekenen ingeschakeld :
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                    <Col sm="6">
                                        <GroupStatus
                                            key={generalSettings?.bGmDistCalEnabled}
                                            data={generalSettings?.bGmDistCalEnabled == true ? 1 : 0}
                                            iconChangeHandler={(field, data) =>
                                                handleSettingIconChange("bGmDistCalEnabled", data)
                                            }
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col sm="6">
                                        <FormGroup>
                                            <Label for="password_current">
                                                Mobile-api :
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                    <Col sm="6">
                                        <GroupStatus
                                            key={generalSettings?.bMobileApiEnabled}
                                            data={generalSettings?.bMobileApiEnabled == true ? 1 : 0}
                                            iconChangeHandler={(field, data) =>
                                                handleSettingIconChange("bMobileApiEnabled", data)
                                            }
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col sm="6">
                                        <FormGroup>
                                            <Label for="password_current">
                                                Power Bi :
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                    <Col sm="6">
                                        <GroupStatus
                                            key={generalSettings?.bPowerBiEnabled}
                                            data={generalSettings?.bPowerBiEnabled == true ? 1 : 0}
                                            iconChangeHandler={(field, data) =>
                                                handleSettingIconChange("bPowerBiEnabled", data)
                                            }
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col sm="6">
                                        <FormGroup>
                                            <Label for="password_current">
                                                Afas get-connector :
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                    <Col sm="6">
                                        <GroupStatus
                                            key={generalSettings?.bAfasGetConnectorEnabled}
                                            data={generalSettings?.bAfasGetConnectorEnabled == true ? 1 : 0}
                                            iconChangeHandler={(field, data) =>
                                                handleSettingIconChange("bAfasGetConnectorEnabled", data)
                                            }
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col sm="6">
                                        <FormGroup>
                                            <Label for="password_current">
                                                Afas update-connector :
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                    <Col sm="6">
                                        <GroupStatus
                                            key={generalSettings?.bAfasUpdateConnectorEnabled}
                                            data={generalSettings?.bAfasUpdateConnectorEnabled == true ? 1 : 0}
                                            iconChangeHandler={(field, data) =>
                                                handleSettingIconChange("bAfasUpdateConnectorEnabled", data)
                                            }
                                        />
                                    </Col>
                                </Row>

                            </CardBody>
                        </Card>
                        <div className='general-setting'>

                            <Card className="main-card mb-0 " style={{ height: '100%' }}>
                                <CardBody>
                                    <p className="py-2 mb-3">Login</p>
                                    <Row>
                                        <Col sm="6">
                                            <FormGroup>
                                                <Label for="password_current">
                                                    2FA ingeschakeld :
                                                </Label>
                                            </FormGroup>
                                        </Col>
                                        <Col sm="6">
                                            <GroupStatus
                                                key={generalSettings?.bTwoFaEnabled}
                                                data={generalSettings?.bTwoFaEnabled == true ? 1 : 0}
                                                iconChangeHandler={(field, data) =>
                                                    handleSettingIconChange("bTwoFaEnabled", data)
                                                }
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col sm="6">
                                            <FormGroup>
                                                <Label for="password_current">
                                                    2FA verplicht voor administrators :
                                                </Label>
                                            </FormGroup>
                                        </Col>
                                        <Col sm="6">
                                            <GroupStatus
                                                key={generalSettings?.bTwoFaMandatoryForAdministrators}
                                                data={generalSettings?.bTwoFaMandatoryForAdministrators == true ? 1 : 0}
                                                iconChangeHandler={(field, data) =>
                                                    handleSettingIconChange("bTwoFaMandatoryForAdministrators", data)
                                                }
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col sm="6">
                                            <FormGroup>
                                                <Label for="password_current">
                                                    Maximum aantal inlogpogingen :
                                                </Label>
                                            </FormGroup>
                                        </Col>
                                        <Col sm="6">
                                            <FormGroup>
                                                <Label for="password_current">
                                                    {generalSettings?.iMaximumLoginAttempts !== '' ? generalSettings?.iMaximumLoginAttempts : '--'}
                                                </Label>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col sm="6">
                                            <FormGroup>
                                                <Label for="password_current">
                                                    Blokkeer periode (minuten) :
                                                </Label>
                                            </FormGroup>
                                        </Col>
                                        <Col sm="6">
                                            <FormGroup>
                                                <Label for="password_current">
                                                    {generalSettings?.iLockoutPeriodMinutes !== '' ? generalSettings?.iLockoutPeriodMinutes : '--'}
                                                </Label>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                </CardBody>
                            </Card>
                        </div>

                    </div>
                </Col>
            </Row>


            <Row className="mb-4">
                <Col lg={12}>
                    <ScheduledEmails />
                </Col>
            </Row>

            <Row>
                <Col lg={12}>
                    <ScheduledSMS />
                </Col>
            </Row>

            {/* Email Modal */}
            <Modal isOpen={emailModal} className="feedback-modal-style link-form sms-form">
                <ModalHeader>
                    Send Email
                    <div className="cross-icon-color">
                        <i className="nav-link-icon lnr-cross m-1 cursor-pointer" onClick={() => setEmailModal(prev => !prev)}> </i>
                    </div>
                </ModalHeader>
                <ModalBody>
                    <Formik
                        initialValues={{ subject: "", content: "" }}
                        validationSchema={emailDataValidationSchema}
                    >
                        {({ values, handleChange, handleBlur, setErrors, errors }) => (
                            <Form>
                                <FormGroup className="mt-3">
                                    <Label for="subject">
                                        Subject
                                    </Label>
                                    <Input name="subject"
                                        type="text" onChange={handleChange} onBlur={handleBlur}
                                        value={values.subject} />
                                    {errors.subject && <FormFeedback className='d-block'>{errors.subject}</FormFeedback>}
                                </FormGroup>
                                <FormGroup>
                                    <Label for="content" className="d-flex justify-content-between">Content</Label>
                                    <Input type="textarea" name="content" id="content" onChange={handleChange} onBlur={handleBlur}
                                        value={values.content} rows={6} />
                                    {errors.content && <FormFeedback className='d-block'>{errors.content}</FormFeedback>}
                                </FormGroup>
                                <ModalFooter className="d-flex align-items-center justify-content-center border-bottom-0 py-4 mt-4">
                                    <Button className="btn-color-style  opslaan-btn" size="sm" outline color="primary" type="button" onClick={(e) => { e.preventDefault(); handleSubmit("email", values, setErrors) }}>
                                        Indienen
                                    </Button>
                                </ModalFooter>
                            </Form>
                        )}
                    </Formik>
                </ModalBody>
            </Modal>

            {/* SMS Modal */}
            <Modal isOpen={smsModal} className="feedback-modal-style link-form sms-form">
                <ModalHeader>
                    Send SMS
                    <div className="cross-icon-color text-end">
                        <i className="nav-link-icon lnr-cross m-1 cursor-pointer" onClick={() => setSmsModal(prev => !prev)}> </i>
                    </div>
                </ModalHeader>
                <ModalBody>
                    <Formik
                        initialValues={{ smsContent: "" }}
                        validationSchema={smsDataValidationSchema}
                    >
                        {({ values, handleChange, handleBlur, errors, setErrors }) => (
                            <Form>
                                <FormGroup>
                                    <Label for="smsContent" className="d-flex justify-content-between">Content</Label>
                                    <Input type="textarea" name="smsContent" id="smsContent" onChange={handleChange} onBlur={handleBlur}
                                        value={values.smsContent} rows={6} />
                                    {errors.smsContent && <FormFeedback className='d-block'>{errors.smsContent}</FormFeedback>}
                                </FormGroup>
                                <ModalFooter className="d-flex align-items-center justify-content-center border-bottom-0 py-4 mt-4">
                                    <Button className="btn-color-style opslaan-btn" size="sm" outline color="primary" type="button" onClick={(e) => { e.preventDefault(); handleSubmit("sms", values, setErrors) }}>
                                        Indienen
                                    </Button>
                                </ModalFooter>
                            </Form>
                        )}
                    </Formik>
                </ModalBody>
            </Modal>

            {/*Link powerBi */}
            <Modal
                isOpen={showPowerBiModal}
                toggle={() => setShowPowerBiModal(false)}
                className="modal-centered contract-type-modal feedback-modal-style link-form"
            >
                <ModalHeader>
                    Link Power BI beheren
                </ModalHeader>

                <ModalBody className="text-start mt-4 mb-4">
                    <p style={{ fontSize: "12px" }}>
                        Plak hieronder de Power BI-link die voor rapportage gebruikt wordt.
                    </p>

                    <div className="flex-column align-items-center justify-content-start">
                        <Input
                            name="Power BI-link"
                            data-lpignore="true"
                            autoComplete="off"
                            type="text"
                            className="w-75 d-flex justify-content-center"
                            value={powerBiLink.link}
                            onChange={(e) => textChangeHandler(e, "link")}
                        />

                        {powerBiLink.linkError && (
                            <div className="text-danger">
                                {powerBiLink.linkError}
                            </div>
                        )}
                    </div>
                </ModalBody>

                <ModalFooter className="justify-content-center pt-3">
                    <Button
                        color="link"
                        className="btn-color-style"
                        onClick={() => setShowPowerBiModal(false)}
                    >
                        Annuleren
                    </Button>

                    <Button
                        color="link"
                        className="btn-color-style"
                        onClick={saveLinkHandler}
                        disabled={isMutating}
                    >
                        {isMutating ? "Opslaan..." : "Toepassen"}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Redirect SMS Modal */}
            <Modal
                isOpen={showRedirectSmsModal}
                toggle={() => setShowRedirectSmsModal(false)}
                className="modal-centered contract-type-modal feedback-modal-style link-form"
            >
                <ModalHeader >
                    Redirect SMS-nummer beheren
                </ModalHeader>
                <ModalBody className=" mt-2 mb-4">
                    <p style={{ fontSize: "12px" }}>
                        Beheer het telefoonnummer waar redirect-SMS-berichten naartoe worden gestuurd.
                    </p>
                    <Label style={{ fontSize: "12px", fontWeight: 600 }} className='mb-2'>Telefoonnummer</Label>

                    <div className="d-flex flex-column align-items-center justify-content-center w-100">

                        <div className="position-relative w-100 d-flex align-items-center">
                            <Input
                                name="redirectSms"
                                data-lpignore="true"
                                autoComplete="off"
                                type="text"
                                value={redirectSms}
                                onChange={(e) => setRedirectSms(e.target.value)}
                                className="w-100 pe-5"
                            />

                            <div
                                className="position-absolute end-0 me-3 cursor-pointer"
                                style={{
                                    zIndex: 5,
                                    top: "50%",
                                    transform: "translateY(-50%)"
                                }}
                                onClick={() => setRedirectSms("")}
                            >
                                <DeleteIcon />
                            </div>
                        </div>

                        {error && <div className="text-danger mt-1">{error}</div>}
                    </div>
                    <p className='mt-2' style={{ fontSize: "12px" }}>Veld leeg opslaan = geen redirect SMS-nummer ingesteld.</p>
                </ModalBody>
                <ModalFooter className="justify-content-center pt-3">
                    <Button
                        color="link"
                        className="btn-color-style"
                        onClick={() => setShowRedirectSmsModal(false)}
                    >
                        Annuleren
                    </Button>

                    <Button
                        color="link"
                        className="btn-color-style"
                        onClick={handleApplyRedirectSms}
                    >
                        Toepassen
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Redirect Email Modal */}
            <Modal
                isOpen={showRedirectEmailModal}
                toggle={() => setShowRedirectEmailModal(false)}
                className="modal-centered contract-type-modal feedback-modal-style link-form"
            >
                <ModalHeader >
                    Redirect e-mailadressen beheren
                </ModalHeader>
                <ModalBody className="text-center mt-2 mb-4">
                    <p style={{ fontSize: "12px" }}>
                        Beheer de e-mailadressen waar redirectberichten naartoe worden gestuurd.
                    </p>
                    <Label className='d-flex' style={{ fontSize: "12px", fontWeight: 600 }}>E-mailadressen</Label>
                    <div className="d-flex flex-column gap-2 w-100">

                        {redirectEmails.map((email, index) => (
                            <div
                                key={`${email}-${index}`}
                                className="position-relative w-100 d-flex align-items-center"
                            >
                                <Input
                                    value={email}
                                    type="text"
                                    readOnly
                                    className="w-100 pe-5 mt-2"
                                />

                                <div
                                    className="position-absolute end-0 me-3 cursor-pointer"
                                    style={{
                                        zIndex: 5,
                                        top: "50%",
                                        transform: "translateY(-50%)"
                                    }}
                                    onClick={() => handleDeleteRedirectEmail(index)}
                                >
                                    <DeleteIcon />
                                </div>
                            </div>
                        ))}

                    </div>

                    <Label className='d-flex mt-3' style={{ fontSize: "12px", fontWeight: 600 }}>Nieuw e-mailadres</Label>
                    <div className="d-flex flex-row gap-2 align-items-start w-100">

                        <div className="d-flex flex-column w-100">
                            <div className="position-relative w-100 d-flex align-items-center">

                                <Input
                                    name="newRedirectEmail"
                                    type="text"
                                    value={newRedirectEmail}
                                    onChange={(e) => {
                                        setNewRedirectEmail(e.target.value);
                                        setRedirectEmailError("");
                                    }}
                                    className="w-100"
                                    placeholder="Nieuw e-mailadres"
                                />

                            </div>

                            {redirectEmailError && (
                                <FormFeedback className="d-block">
                                    {redirectEmailError}
                                </FormFeedback>
                            )}
                        </div>

                        <Button
                            color="link"
                            className="btn-color-style"
                            type="button"
                            onClick={handleAddRedirectEmail}
                        >
                            Toevoegen
                        </Button>

                    </div>
                </ModalBody>
                <ModalFooter className="justify-content-center pt-3">
                    <Button
                        color="link"
                        className="btn-color-style"
                        onClick={() => setShowRedirectEmailModal(false)}
                    >
                        Annuleren
                    </Button>

                    <Button
                        color="link"
                        className="btn-color-style"
                        onClick={handleApplyRedirectEmails}
                    >
                        Toepassen
                    </Button>
                </ModalFooter>
            </Modal>

        </>
    )
}

export default GeneralSettingsForm;
